import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { MoroccoMap } from '../../../ComponnentsUI/MoroccoMap';
import { switchScope, useMyScopes } from '../../../../helpers/acl/facility';
import { HomeKpisFacilityComparisonItem } from '../../../interfaces/homeKpis';

const CENTER_ID_TO_MATCH: Record<string, string[]> = {
  aghbala: ['aghbala'],
  'ait-tamlil': ['ait tamlil', 'aït tamlil', 'tamlil'],
  anergui: ['anergui'],
  boutferda: ['boutferda'],
  'el-ksiba': ['el ksiba', 'ksiba'],
  'moula-bouaaza': ['moulay bouazza', 'moula bouaaza', 'bouazza'],
  ahansal: ['ahansal', 'zaouiat'],
  taguelft: ['taguelft'],
};

export const MoroccoMapPanel: React.FC<{
  facilities: HomeKpisFacilityComparisonItem[];
}> = ({ facilities }) => {
  const { t } = useTranslation('Dashboard');
  const qc = useQueryClient();
  const { data: scopes } = useMyScopes();

  const centerLookup = useMemo(() => {
    const accessibleIds = new Set((scopes?.facilities ?? []).map((f) => f.id));
    const idx: Record<
      string,
      HomeKpisFacilityComparisonItem & { canScope: boolean }
    > = {};
    for (const [centerId, needles] of Object.entries(CENTER_ID_TO_MATCH)) {
      const match = facilities.find((f) => {
        const n = (f.facility_name || '').toLowerCase();
        return needles.some((k) => n.includes(k));
      });
      if (match) {
        idx[centerId] = {
          ...match,
          canScope: accessibleIds.has(match.facility_id),
        };
      }
    }
    return idx;
  }, [facilities, scopes]);

  // The MoroccoMap looks up region colors via findData() matching r.region
  // against each region's `name`. Facilities live in Béni Mellal-Khénifra, so
  // we pass a single row covering that region, colored by patient volume.
  const regionalData = useMemo(() => {
    const totalPatients = facilities.reduce(
      (sum, f) => sum + f.total_patients,
      0
    );
    return [
      {
        region: 'Béni Mellal',
        mmr: Math.min(100, Math.round((totalPatients / 220) * 100)),
        total_patients: totalPatients,
      },
    ];
  }, [facilities]);

  const onSelectCenter = (center: { id: string }) => {
    const facility = centerLookup[center.id];
    if (!facility || !facility.canScope) return;
    switchScope(qc, { kind: 'facility', id: facility.facility_id });
  };

  const onClear = () => {
    // Clearing to an org node (not null) keeps the sidebar selector in sync -
    // useCurrentFacility falls back to facilities[0] when nothing is stored,
    // which would show the wrong current scope in the top-left selector.
    const orgNode = scopes?.orgNodes?.[0];
    switchScope(qc, orgNode ? { kind: 'org', id: orgNode.id } : null);
  };

  return (
    <div className="relative h-[720px] w-full">
      <button
        type="button"
        onClick={onClear}
        className="absolute right-4 top-4 z-40 rounded-full border border-blue-200 bg-white/95 backdrop-blur px-4 py-2 text-xs font-bold uppercase tracking-widest text-blue-700 shadow-md hover:bg-blue-50"
      >
        {t('kpis.map.clearFilter')}
      </button>
      <MoroccoMap
        onSelectCenter={onSelectCenter}
        activeId={null}
        regionalData={regionalData}
        centerData={Object.fromEntries(
          Object.entries(centerLookup).map(([id, f]) => [
            id,
            { total_patients: f.total_patients },
          ])
        )}
      />
    </div>
  );
};
